import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import AdminManagement from './index'
import { GenericForm } from './GenericForm'
import { Stethoscope } from 'lucide-react'

import { buildSuggestedUsername } from './usernameHelpers'

const OphthalmologistsManagement = () => {
  const [institutions, setInstitutions] = useState([])
  const { fetchEntities } = useAdminAPI()
  const userRole = useSelector(state => state.user.role)
  const isAdminInstitucion = userRole === 'admin_institucion'

  useEffect(() => {
    let isMounted = true
    if (isAdminInstitucion) return

    const loadInstitutions = async () => {
      try {
        const result = await fetchEntities('instituciones')
        if (isMounted) {
          const items = Array.isArray(result) ? result : result.data || []
          setInstitutions(items)
        }
      } catch (error) {
        console.error('Failed to load institutions', error)
      }
    }
    loadInstitutions()
    return () => { isMounted = false }
  }, [fetchEntities, isAdminInstitucion])

  const columns = useMemo(() => {
    const cols = [
      { key: 'name', label: 'Nombre', render: (item) => `${item.nombre} ${item.apellido}` },
      { key: 'email', label: 'Email' },
      { key: 'dni', label: 'DNI' },
      { key: 'matricula', label: 'Matrícula', render: (item) => item.oftalmologo?.matricula || ' - ' }
    ]

    if (!isAdminInstitucion) {
      cols.push({
        key: 'institucion',
        label: 'Institución',
        render: (item) => {
          const instId = item.oftalmologo?.idInstitucion
          const instName = institutions.find(i => i.idInstitucion === instId)?.nombre || ' - '
          return instName
        }
      })
    }
    return cols
  }, [institutions, isAdminInstitucion])

  const FormComponent = (props) => {
    const { initialData } = props
    const [usernameEditedManually, setUsernameEditedManually] = useState(Boolean(initialData?.username))

    const flattenedData = useMemo(() => {
      if (!initialData) return null
      return {
        ...initialData,
        matricula: initialData.oftalmologo?.matricula || initialData.matricula,
        idInstitucion: initialData.oftalmologo?.idInstitucion || initialData.idInstitucion
      }
    }, [initialData])

    useEffect(() => {
      setUsernameEditedManually(Boolean(initialData?.username))
    }, [initialData])

    const handleFieldChange = useCallback((name, value, updatedData) => {
      if (name === 'username') {
        setUsernameEditedManually((value || '').trim() !== '')
        return updatedData
      }

      if (initialData || usernameEditedManually) return updatedData
      if (name !== 'nombre' && name !== 'apellido') return updatedData

      const suggestedUsername = buildSuggestedUsername(updatedData.nombre, updatedData.apellido)
      if (!suggestedUsername) return updatedData

      return {
        ...updatedData,
        username: suggestedUsername
      }
    }, [initialData, usernameEditedManually])

    const formFields = useMemo(() => {
      const fields = [
        { key: 'dni', label: 'DNI', type: 'number', required: true },
        {
          key: 'genero',
          label: 'Género',
          required: true,
          type: 'select',
          options: [
            { value: 'Masculino', label: 'Masculino' },
            { value: 'Femenino', label: 'Femenino' }
          ]
        },
        { key: 'nombre', label: 'Nombre', required: true },
        { key: 'apellido', label: 'Apellido', required: true },
        { key: 'username', label: 'Usuario', required: true },
        { key: 'email', label: 'Correo electrónico', type: 'email', required: true },
        { key: 'clave', label: 'Contraseña', type: 'password', required: !initialData },
        {
          key: 'confirmarClave',
          label: 'Repetir contraseña',
          type: 'password',
          required: !initialData,
          matchesField: 'clave',
          errorMessage: 'Las contraseñas no coinciden.',
          clientOnly: true
        },
        { key: 'matricula', label: 'Matrícula', required: true }
      ]

      if (!isAdminInstitucion) {
        // Insert Institution field right before matricula
        fields.splice(fields.length - 1, 0, {
          key: 'idInstitucion',
          label: 'Institución',
          type: 'select',
          required: true,
          options: institutions.map(i => ({
            value: i.idInstitucion,
            label: `${i.nombre} - ${i.calle} ${i.numero}`
          }))
        })
      }

      return fields.filter(f => !initialData || !['clave', 'confirmarClave'].includes(f.key))
    }, [initialData, institutions, isAdminInstitucion])

    return (
      <GenericForm
        {...props}
        initialData={flattenedData}
        fields={formFields}
        title='Oftalmólogo'
        createTitle='Nuevo Oftalmólogo'
        onFieldChange={handleFieldChange}
      />
    )
  }

  return (
    <AdminManagement
      entityType='Oftalmólogos'
      headerSubtitle={isAdminInstitucion ? 'Desde acá podés administrar usuarios oftalmólogos asociados a tu institución.' : 'Desde acá podés administrar usuarios oftalmólogos y asignarlos a las instituciones para las que informarán estudios.'}
      HeaderIcon={Stethoscope}
      columns={columns}
      apiEndpoint='usuarios/retinologos'
      FormComponent={FormComponent}
      gridTemplateColumns={isAdminInstitucion ? '1fr 1.5fr 1fr 1fr 0.5fr' : '1fr 1.5fr 1fr 1fr 1fr 0.5fr'}
      singularEntityName='Oftalmólogo'
    />
  )
}

export default OphthalmologistsManagement
